import { Button } from '@/components/ui/Button';
import { mdiPlay } from '@mdi/js';
import * as React from 'react';
import type { Provider } from '../hooks/useImportJobs';

interface UrlInputProps {
  providers: Provider[];
  selectedProvider: string;
  onProviderChange: (provider: string) => void;
  urls: string;
  onUrlsChange: (value: string) => void;
  selectedStatus: string;
  onStatusChange: (status: string) => void;
  onSubmit: () => void;
  isLoading: boolean;
}

export const UrlInput: React.FC<UrlInputProps> = ({
  providers,
  selectedProvider,
  onProviderChange,
  urls,
  onUrlsChange,
  selectedStatus,
  onStatusChange,
  onSubmit,
  isLoading,
}) => {
  const parsedUrls = React.useMemo(() => {
    if (!urls.trim()) return [];
    return urls
      .split(/[\n]+/)
      .map(url => url.trim())
      .filter(url => url.length > 0 && url.startsWith('http'));
  }, [urls]);

  const selectedProviderData = providers.find(
    p => p.identifier === selectedProvider,
  );
  const isConfigured = selectedProviderData?.configured ?? false;

  const handleSubmit = () => {
    const statusLabel =
      selectedStatus === 'Pending' ? 'pending review' : 'active (published)';
    const confirmed = window.confirm(
      `Are you sure you want to import ${parsedUrls.length} place${parsedUrls.length !== 1 ? 's' : ''}?\n\n` +
        `• Status: ${statusLabel}\n` +
        `• Categories will be automatically inferred from Google\n` +
        `• Filters will NOT be assigned (they come from reviews only)\n` +
        `• Places missing name, address, photos, or opening hours will be skipped`,
    );
    if (confirmed) {
      onSubmit();
    }
  };

  return (
    <div className='space-y-4'>
      <div>
        <label className='block text-sm font-medium text-grey-700 mb-1'>
          Map Provider
        </label>
        <select
          value={selectedProvider}
          onChange={e => onProviderChange(e.target.value)}
          className='w-full px-3 py-2 border border-grey-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
        >
          {providers.map(provider => (
            <option key={provider.identifier} value={provider.identifier}>
              {provider.name}
              {!provider.configured && ' (Not Configured)'}
            </option>
          ))}
        </select>
        {!isConfigured && (
          <p className='mt-1 text-sm text-yellow-600'>
            This provider is not configured. Please set up the API key.
          </p>
        )}
      </div>

      <div>
        <label className='block text-sm font-medium text-grey-700 mb-1'>
          Map URLs
        </label>
        <textarea
          value={urls}
          onChange={e => onUrlsChange(e.target.value)}
          placeholder={`Paste ${selectedProviderData?.name || 'map'} URLs here, one per line...

Example:
https://maps.app.goo.gl/abc123
https://www.google.com/maps/place/Coffee+Shop/@40.7128,-74.0060,17z/...`}
          className='w-full h-64 px-3 py-2 border border-grey-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm'
        />
        <div className='mt-2 flex justify-between items-center'>
          <p className='text-sm text-grey-500'>
            {parsedUrls.length > 0 ? (
              <>
                <span className='font-semibold text-blue-600'>
                  {parsedUrls.length.toLocaleString()}
                </span>{' '}
                URL{parsedUrls.length !== 1 ? 's' : ''} detected
              </>
            ) : (
              'Paste URLs to import'
            )}
          </p>
        </div>
      </div>

      <div className='bg-grey-50 border border-grey-200 rounded-lg p-4'>
        <h4 className='text-sm font-medium text-grey-800 mb-2'>
          Import Status
        </h4>
        <p className='text-xs text-grey-500 mb-3'>
          Choose the status for imported businesses
        </p>
        <div className='flex items-center space-x-6'>
          <label className='flex items-center cursor-pointer'>
            <input
              type='radio'
              name='status'
              value='Pending'
              checked={selectedStatus === 'Pending'}
              onChange={e => onStatusChange(e.target.value)}
              className='mr-2 text-blue-600 focus:ring-blue-500'
            />
            <span className='text-sm text-grey-700'>Pending</span>
            <span className='ml-1 text-xs text-grey-500'>
              (Review before publishing)
            </span>
          </label>
          <label className='flex items-center cursor-pointer'>
            <input
              type='radio'
              name='status'
              value='Active'
              checked={selectedStatus === 'Active'}
              onChange={e => onStatusChange(e.target.value)}
              className='mr-2 text-blue-600 focus:ring-blue-500'
            />
            <span className='text-sm text-grey-700'>Active</span>
            <span className='ml-1 text-xs text-grey-500'>
              (Publish immediately)
            </span>
          </label>
        </div>
      </div>

      {selectedProviderData && (
        <div className='bg-grey-50 border border-grey-200 rounded-lg p-4'>
          <h4 className='text-sm font-medium text-grey-800 mb-2'>
            How to get URLs from {selectedProviderData.name}:
          </h4>
          <ol className='text-sm text-grey-600 list-decimal list-inside space-y-1'>
            <li>Open a place in {selectedProviderData.name}</li>
            <li>Click the share button and copy the link</li>
            <li>Paste each URL on a new line</li>
            <li>Both short links (maps.app.goo.gl) and full URLs work</li>
          </ol>
        </div>
      )}

      <div className='flex justify-end'>
        <Button
          label='Import'
          icon={mdiPlay}
          color='primary'
          onClick={handleSubmit}
          disabled={parsedUrls.length === 0 || !isConfigured || isLoading}
        />
      </div>
    </div>
  );
};
